import { useMemo } from 'react';
import { Button } from 'src/components/Button';
import { Loading } from 'src/components/Loading';
import { Option, SelectBox } from 'src/components/SelectBox';
import { DAY_OF_WEEK_TO_UTC_DAY_MAPPING } from 'src/services/campaigns/campaigns.service';
import type {
  DayOfWeek,
  WorkflowContactWindow,
} from 'src/interfaces/workflows.interface';
import { WorkflowTimezone } from 'src/interfaces/workflows.interface';
import { SchedulePreview } from './SchedulePreview';

type LegacySchedule = {
  days: DayOfWeek[];
  startTime: string;
  endTime: string;
  timeZoneOption: Option;
};

type ScheduleSelectorProps = {
  // Contact window data
  contactWindows: WorkflowContactWindow[];
  selectedContactWindow: WorkflowContactWindow | null;
  onContactWindowChange: (cw: WorkflowContactWindow | null) => void;
  isLoading?: boolean;

  // For legacy mode (readonly)
  legacySchedule?: LegacySchedule;

  // Navigation callback - consumer controls URL building
  onNavigateToScheduleEditor: (contactWindowId?: string) => void;
};

const LEGACY_SCHEDULE_OPTION: Option = {
  label: 'Legacy Schedule',
  value: '__legacy__',
};

export function ScheduleSelector({
  contactWindows,
  selectedContactWindow,
  onContactWindowChange,
  isLoading = false,
  legacySchedule,
  onNavigateToScheduleEditor,
}: ScheduleSelectorProps) {
  const isLegacyMode = !selectedContactWindow && !!legacySchedule;

  const contactWindowOptions = useMemo(() => {
    const options = contactWindows.map((record) => ({
      label: record.label,
      value: record.id,
    }));
    return options;
  }, [contactWindows]);

  const allOptions = useMemo(() => {
    if (isLegacyMode) {
      return [LEGACY_SCHEDULE_OPTION, ...contactWindowOptions];
    }
    return contactWindowOptions;
  }, [isLegacyMode, contactWindowOptions]);

  const selectedOption = useMemo(() => {
    if (isLegacyMode) {
      return LEGACY_SCHEDULE_OPTION;
    }
    if (selectedContactWindow) {
      return {
        label: selectedContactWindow.label,
        value: selectedContactWindow.id,
      };
    }
    return { label: 'Select campaign schedule', value: '' };
  }, [isLegacyMode, selectedContactWindow]);

  const handleSelectChange = (value: string) => {
    if (value === LEGACY_SCHEDULE_OPTION.value) {
      // Can't select legacy - it's readonly
      return;
    }
    const matchingRecord = contactWindows.find((record) => record.id === value);
    onContactWindowChange(matchingRecord || null);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex gap-2 items-center py-2">
        <Loading size="6" />
        <span className="text-neutral-400 text-sm">
          Loading campaign schedules...
        </span>
      </div>
    );
  }

  // Legacy schedule mode - readonly
  if (isLegacyMode) {
    return (
      <div>
        <div className="flex flex-row gap-2 justify-between">
          <div className="flex gap-3 items-center w-full">
            <SelectBox
              className="w-full bg-neutral-0"
              options={allOptions}
              value={selectedOption}
              onChange={handleSelectChange}
            />
          </div>
          <Button
            variant="outlined"
            className="bg-neutral-0 shrink-0 whitespace-nowrap"
            onClick={() => onNavigateToScheduleEditor()}
            size="small"
          >
            Create campaign schedule
          </Button>
        </div>

        <SchedulePreview
          days={legacySchedule.days.map(
            (day) => DAY_OF_WEEK_TO_UTC_DAY_MAPPING[day],
          )}
          startTime={legacySchedule.startTime}
          endTime={legacySchedule.endTime}
          timeZone={legacySchedule.timeZoneOption.value as WorkflowTimezone}
          isReadOnly
        />

        <div className="mt-2 text-sm text-warning-500">
          Legacy schedules cannot be edited. Select or create a campaign
          schedule to enable editing.
        </div>
      </div>
    );
  }

  // No contact windows and no legacy - show empty state
  if (contactWindowOptions.length === 0) {
    return (
      <div className="mt-3 rounded border bg-neutral-0 p-3 text-sm text-neutral-700">
        <div className="flex items-center justify-between gap-2">
          <span>No schedules yet — add one to continue.</span>
          <Button
            className="shrink-0 whitespace-nowrap"
            size="small"
            onClick={() => onNavigateToScheduleEditor()}
          >
            Create campaign schedule
          </Button>
        </div>
      </div>
    );
  }

  // Contact window mode
  return (
    <div>
      <div className="flex flex-row gap-2 justify-between">
        <div className="flex gap-3 items-center w-full">
          <SelectBox
            className="w-full bg-neutral-0"
            options={allOptions}
            value={selectedOption}
            onChange={handleSelectChange}
          />
        </div>
        {selectedContactWindow && (
          <Button
            variant="outlined"
            className="bg-neutral-0 shrink-0 whitespace-nowrap"
            onClick={() => onNavigateToScheduleEditor()}
            size="small"
          >
            Create another schedule
          </Button>
        )}
      </div>

      {selectedContactWindow ? (
        <SchedulePreview
          days={selectedContactWindow.days}
          startTime={selectedContactWindow.startTime}
          endTime={selectedContactWindow.endTime}
          timeZone={selectedContactWindow.primaryTz}
          fallbackTimeZone={selectedContactWindow.fallbackTz}
          onEditClick={() =>
            onNavigateToScheduleEditor(selectedContactWindow.id)
          }
        />
      ) : (
        <div className="mt-3 rounded border bg-neutral-0 p-3 text-sm text-neutral-700">
          <div className="flex items-center justify-between gap-2">
            <span>Select a schedule above, or create a new one.</span>
            <Button
              className="shrink-0 whitespace-nowrap"
              size="small"
              onClick={() => onNavigateToScheduleEditor()}
            >
              Create campaign schedule
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
